/***
*
*   USERS
*   Manage individual users registered to your application
*
**********/

import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Card, Table, Input, Space, Typography, Button, Tag, Tooltip,
  // Modal,
  message, Avatar, Row, Col, Statistic, Select
} from 'antd';
import {
  UserOutlined, SearchOutlined, EyeOutlined, EditOutlined,
  // DeleteOutlined,
  LoginOutlined, MailOutlined, ReloadOutlined,
  CheckCircleOutlined, CloseCircleOutlined, TeamOutlined
} from '@ant-design/icons';
import Axios from 'axios';
import Settings from 'settings.json';
import {
  fetchUsers,
  // fetchUserEmails, 
  invalidateUsersCache
} from '../store/slices/usersSlice';
import {
  Animate, ViewContext,
  // AuthContext, 
  usePermissions
} from 'components/lib';


const { Title, Text } = Typography;
// const { confirm } = Modal;
const { Option } = Select;

export function Users() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { can } = usePermissions();

  const viewContext = useContext(ViewContext);
  // const authContext = useContext(AuthContext);
  const query = window.location.search;

  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [pageSize, setPageSize] = useState(25);

  const usersState = useSelector((state) => state.users);
  const { data: users, loading } = usersState;

  useEffect(() => {
    dispatch(fetchUsers());
    // dispatch(fetchUserEmails());
  }, [dispatch]);

  useEffect(() => {
    if (users?.users) {
      let filtered = users.users;

      // Search filter
      if (searchInput) {
        const search = searchInput.toLowerCase();
        filtered = filtered.filter(user =>
          (user.first_name + ' ' + user.last_name)?.toLowerCase().includes(search) ||
          user.email?.toLowerCase().includes(search)
        );
      }

      // Role filter
      if (roleFilter) {
        filtered = filtered.filter(user =>
          user.workspaces?.some(ws => ws.role_name === roleFilter)
        );
      }

      setFilteredData(filtered);
    }
  }, [users, searchInput, roleFilter]);

  useEffect(() => {
    const urlParams = new URLSearchParams(query);
    const role = urlParams.get('role') || '';
    // make the first letter uppercase to match role names
    if (role) {
      const formattedRole = role.charAt(0).toUpperCase() + role.slice(1);
      setRoleFilter(formattedRole);
    } else {
      setRoleFilter('');
    }
  }, [query]);

  // Extract unique role names from all users
  const uniqueRoles = [...new Set(
    users?.users?.flatMap(user =>
      user.workspaces?.map(ws => ws.role_name) || []
    ).filter(Boolean)
  )].sort();

  const handleRefresh = () => {
    dispatch(invalidateUsersCache());
    dispatch(fetchUsers());
  };

  const handleEdit = (user) => {
    viewContext.modal.show({
      title: 'Edit User',
      form: {
        name: {
          label: 'Name',
          type: 'text',
          value: user.first_name + ' ' + user.last_name,
          required: true,
        },
        email: {
          label: 'Email',
          type: 'email',
          value: user.email,
          required: true
        }
      },
      buttonText: 'Save',
      url: `/api/user/${user.id || user._id}`,
      method: 'PATCH'
    }, () => {
      message.success(`${user.first_name} ${user.last_name} was updated`);
      handleRefresh();
    });
  };

  // const handleDelete = (user) => {
  //   confirm({
  //     title: 'Delete User',
  //     content: `Are you sure you want to delete ${user.email}? This will remove them from all accounts.`,
  //     okText: 'Delete',
  //     okType: 'danger',
  //     onOk: async () => {
  //       try {
  //         message.success(`${user.email} was deleted`);
  //         handleRefresh();
  //       } catch (err) {
  //         message.error('Failed to delete user');
  //       }
  //     },
  //   });
  // };

  const handleImpersonate = async (user) => {
    try {
      const res = await Axios.post(`/api/user/impersonate/${user._id || user.id}`);
      const token = res.data?.data?.token;
      if (token) {
        window.location = Settings[process.env.REACT_APP_YOSUITE_ENV_MODE].remote_client + `/signin/impersonate?token=${token}`;
      }
    } catch (err) {
      message.error('Failed to impersonate user');
    }
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <Space>
          <Avatar
            src={record.avatar}
            icon={!record.avatar && <UserOutlined />}
            style={{ backgroundColor: '#7c3aed' }}
          />
          <div>
            <Text strong>{record.first_name + ' ' + record.last_name || 'N/A'}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Account Type',
      key: 'account_type',
      render: (_, record) => (
        <Space wrap>
          {record.workspaces?.map((ws, idx) => (
            <Tag key={idx} color="purple">{ws.account_type}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Role(s)',
      key: 'roles',
      render: (_, record) => (
        <Space wrap>
          {record.workspaces?.map((ws, idx) => (
            <Tag key={idx} color="purple">{ws.role_name}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Verified',
      dataIndex: 'verified',
      key: 'verified',
      render: (verified) => verified
        ? <Tag color="green" icon={<CheckCircleOutlined />}>Verified</Tag>
        : <Tag color="orange" icon={<CloseCircleOutlined />}>Unverified</Tag>,
    },
    {
      title: 'Onboarded',
      dataIndex: 'onboarded',
      key: 'onboarded',
      render: (onboarded) => onboarded
        ? <Tag color="blue">Yes</Tag>
        : <Tag color="default">No</Tag>,
    },
    {
      title: 'Created',
      dataIndex: 'date_created',
      key: 'date_created',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Last Active',
      dataIndex: 'last_active',
      key: 'last_active',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'Never',
    },
    {
      ...(can('users', 'view') || can('users', 'edit') || can('users', 'delete')) && {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Space>
            {can('users', 'view') && (
              <Tooltip title="View Details">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => navigate(`/users/${record.id || record._id}`)}
                />
              </Tooltip>
            )}
            {can('users', 'edit') && (
              <Tooltip title="Edit">
                <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
              </Tooltip>
            )}
            <Tooltip title="Send Email">
              <Button
                type="text"
                icon={<MailOutlined />}
                onClick={() => window.location.href = `mailto:${record.email}`}
              />
            </Tooltip>
            {can('users', 'edit') && (
              <Tooltip title="Impersonate">
                <Button type="text" icon={<LoginOutlined />} onClick={() => handleImpersonate(record)} />
              </Tooltip>
            )}
            {/* Now we are not allowing to delete users */}
            {/* {can('users', 'delete') && (
              <Tooltip title="Delete">
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
              </Tooltip>
            )} */}
          </Space>
        ),
      }
    },
  ];

  const tableData = filteredData.map(user => ({
    ...user,
    key: user.id || user._id,
  }));

  return (
    <Animate type="pop">
      <div>
        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <TeamOutlined style={{ marginRight: 8 }} />
              Users
            </Title>
            <Text type="secondary">Manage individual users registered to your application</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Refresh</Button>
        </div>

        {/* Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Users"
                value={users?.users?.length || 0}
                prefix={<UserOutlined style={{ color: '#7c3aed' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Verified"
                value={users?.users?.filter(u => u.verified).length || 0}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Unverified"
                value={users?.users?.filter(u => !u.verified).length || 0}
                prefix={<CloseCircleOutlined style={{ color: '#faad14' }} />}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Input
                placeholder="Search users by name or email..."
                prefix={<SearchOutlined />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12}>
              <Select
                placeholder="All Roles"
                allowClear
                style={{ width: '100%' }}
                value={roleFilter || undefined}
                onChange={(val) => setRoleFilter(val || '')}
              >
                {uniqueRoles.map(role => (
                  <Option key={role} value={role}>{role}</Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={tableData}
            loading={loading}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              showTotal: (total) => `Total ${total} users`,
              onShowSizeChange: (current, size) => setPageSize(size),
            }}
          />
        </Card>
      </div>
    </Animate>
  );
}